import debounce from 'lodash.debounce'
import React, {
  FC,
  HTMLAttributes,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useLayoutSnapshot } from 'react-viewport-utils'
import styled, { css } from 'styled-components'
import { hideScrollBar, touchScroll } from 'uiComponents/constants/scrolling'
import VerticalSliderControl from 'uiComponents/molecules/VerticalSliderControl/VerticalSliderControl'

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const Slider = styled.div<{
  snapCentered: boolean
  scrollable: boolean
  showScrollbars: boolean
  disableSnap: boolean
}>`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: ${({ scrollable }) => (scrollable ? 'auto' : 'hidden')};
  scroll-snap-type: ${({ disableSnap }) =>
    disableSnap ? 'none' : 'y mandatory'};
  ${({ showScrollbars }) =>
    !showScrollbars &&
    css`
      ${touchScroll};
      ${hideScrollBar};
    `};

  & > * {
    flex-basis: calc(100%);
    flex-shrink: 0;
    scroll-snap-align: ${({ disableSnap, snapCentered }) =>
      disableSnap ? 'none' : snapCentered ? 'center' : 'start'};
  }
`

const StyledControl = styled(VerticalSliderControl)<{
  controlPosition: ControlPosition
  controlPlacement: ControlPlacement
}>`
  position: ${({ controlPosition }) => controlPosition};
  left: 40px;
  ${({ controlPlacement }) => controlPlacement === 'top-left' && 'top: 40px;'};

  ${({ controlPlacement }) =>
    controlPlacement === 'left-center' &&
    css`
      top: 50%;
      transform: translateY(calc(-55%));
    `};
`

export type ControlColor = 'primary' | 'secondary'
export type ControlPosition = 'absolute' | 'fixed'
export type ControlPlacement = 'top-left' | 'left-center'

interface Props extends HTMLAttributes<HTMLDivElement> {
  controlColor?: ControlColor
  controlPosition?: ControlPosition
  controlPlacement?: ControlPlacement
  disableControls?: boolean
  startIndex?: number
  snapCentered?: boolean
  scrollable?: boolean
  showScrollbars?: boolean
  disableSnap?: boolean
}

const VerticalSlider: FC<Props> = ({
  children,
  controlColor = 'secondary',
  controlPlacement = 'left-center',
  controlPosition = 'absolute',
  startIndex,
  snapCentered = false,
  scrollable = true,
  showScrollbars = false,
  disableSnap = false,
  disableControls = false,
  ...props
}) => {
  const [heights, setHeights] = useState<number[]>([])
  const slider = useRef<HTMLDivElement>(null)
  const slideCount = React.Children.count(children)
  const { next, prev, currentPage } = useSlider(
    slider,
    startIndex,
    heights,
    slideCount,
    snapCentered,
  )

  useEffect(() => {
    // Why requestAnimationFrame: Otherwise we run into a race condition on IE11
    const id = requestAnimationFrame(() => {
      if (slider.current) {
        const nextHeights = Array.from(slider.current.children).map(node => {
          return (node as HTMLElement).clientHeight
        })

        setHeights(nextHeights)
      }
    })

    return () => {
      cancelAnimationFrame(id)
    }
  }, [])

  return (
    <Wrapper {...props}>
      <Slider
        showScrollbars={showScrollbars}
        scrollable={scrollable}
        snapCentered={snapCentered}
        disableSnap={disableSnap}
        ref={slider}
      >
        {children}
      </Slider>
      {slideCount > 1 && !disableControls && (
        <StyledControl
          color={controlColor}
          controlPosition={controlPosition}
          controlPlacement={controlPlacement}
          next={next}
          back={prev}
          slideCount={slideCount}
          currentIndex={currentPage}
        />
      )}
    </Wrapper>
  )
}

const getScrollTopForIndex = (
  slideHeights: number[],
  index: number,
  snapCentered: boolean,
) => {
  let sumCounter = 0
  let targetScrollTop = 0
  for (const slideHeight of slideHeights) {
    if (sumCounter >= index) {
      break
    }
    targetScrollTop += slideHeight
    sumCounter++
  }

  return snapCentered
    ? targetScrollTop + slideHeights[index] / 4
    : targetScrollTop
}

const scrollTo = (
  sliderRef: RefObject<HTMLElement>,
  slideHeights: number[],
  currentIndex: number,
  slideCount: number,
  direction: 'start' | 'end',
  snapCentered: boolean,
) => {
  if (!sliderRef.current) {
    return
  }
  const targetIndex =
    direction === 'start'
      ? currentIndex - 1 < 0
        ? slideCount - 1
        : currentIndex - 1
      : (currentIndex + 1) % slideCount

  const nextY = getScrollTopForIndex(slideHeights, targetIndex, snapCentered)

  if (!sliderRef.current.scrollTo) {
    sliderRef.current.scrollTop = nextY
  } else {
    sliderRef.current.scrollTo({
      top: nextY,
      left: 0,
      behavior: 'smooth',
    })
  }
}

const setStartIndex = (
  sliderRef: RefObject<HTMLElement>,
  slideHeights: number[],
  startIndex: number,
  snapCentered: boolean,
) => {
  if (!sliderRef.current) {
    return
  }

  sliderRef.current.scrollTop = getScrollTopForIndex(
    slideHeights,
    startIndex,
    snapCentered,
  )
}
const useSlider = (
  sliderRef: RefObject<HTMLDivElement>,
  startIndex: number | undefined,
  slideHeights: number[],
  slideCount: number,
  snapCentered: boolean,
) => {
  const scrollHeight =
    useLayoutSnapshot(
      () => (sliderRef.current ? sliderRef.current.scrollHeight : 0),
      updateConf,
      [sliderRef.current],
    ) || NaN
  const scrollY = useScrollY(sliderRef)
  const scrollRelativeY = scrollY / scrollHeight
  const currentPage = Math.max(
    Math.min(Math.round(scrollRelativeY * slideCount), slideCount - 1),
    0,
  )

  const next = useCallback(
    () =>
      scrollTo(
        sliderRef,
        slideHeights,
        currentPage,
        slideCount,
        'end',
        snapCentered,
      ),
    [slideHeights, sliderRef.current, currentPage, slideCount],
  )
  const prev = useCallback(
    () =>
      scrollTo(
        sliderRef,
        slideHeights,
        currentPage,
        slideCount,
        'start',
        snapCentered,
      ),
    [slideHeights, sliderRef.current, currentPage, slideCount],
  )

  useEffect(() => {
    if (startIndex !== undefined) {
      setStartIndex(sliderRef, slideHeights, startIndex, snapCentered)
    }
  }, [sliderRef.current, slideHeights, startIndex, snapCentered])

  return {
    next,
    prev,
    scrollRelativeY,
    currentPage,
  }
}

const useScrollY = (element: React.RefObject<HTMLDivElement>) => {
  const [scrollY, setScrollY] = useState(0)
  useEffect(() => {
    const updateScroll = debounce(
      (ev: Event) =>
        setScrollY(ev.target ? (ev.target as HTMLDivElement).scrollTop : 0),
      50,
    )
    if (element.current) {
      element.current.addEventListener('scroll', updateScroll, {
        passive: true,
      })
    }
    return () => {
      if (!element.current) {
        return
      }
      element.current.removeEventListener('scroll', updateScroll)
    }
  }, [element.current])
  return scrollY
}

const updateConf = {
  disableScrollUpdates: true,
  priority: 'low',
} as const

export default VerticalSlider
